import {
  AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit,
  Output
} from '@angular/core';
import {BehaviorSubject, Subject} from "rxjs";
import {
  TranslationFormModel, TranslationModel, TranslationProgressMentStatus,
  TranslationSupportiveLanguage
} from "../../models/translation.model";
import {TranslationUIService} from "../../services/translation-ui.service";
import {TranslationService} from "../../services/translation.service";
import {takeUntil} from "rxjs/operators";
import {IQueryState} from "@shared/store/reducers/query.reducer";
import {select, Store} from "@ngrx/store";
import {getQueryResponse} from "@shared/store/selectors/query.selector";
import {QueryProvider} from "@shared/queries/query-provider";
import {DialogModel} from "@shared/models/dialog.model";
import {DialogService} from "@shared/services/dialog-service";
import {
  TranslationForms, TranslationFormsFrameId, TranslationStatus,
  TranslationToolNames
} from "../../models/translation.constants";
import {TranslationCurrencyConversionService} from "../../services/translation-currency-conversion.service";
import {ProgressBarService} from "@core/services/progress-bar.service";
import {formatNumber} from "@angular/common";
import {StringUtility} from "@shared/utility/string-utility";
import {CommandService} from "@core/services/command.service";
import {SnackBarService} from "@core/services/snack-bar.service";
import { FuseSplashScreenService } from '@barebone-fuse-services/splash-screen.service';
import {ConvertHtmlToPdfCommand} from "../../commands/translation.commands";

@Component({
    standalone: false,
    selector: 'particle-translated-form',
  templateUrl: './translated-form.component.html',
  styleUrls: ['./translated-form.component.scss']
})
export class TranslatedFormComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() formInfo$: BehaviorSubject<TranslationFormModel> = new BehaviorSubject(new TranslationFormModel());
  @Input() translationSupportiveLanguage: TranslationSupportiveLanguage[] = [];
  @Input() translationModel: TranslationModel = new TranslationModel();
  @Input() progressStatus$: BehaviorSubject<TranslationProgressMentStatus> = new BehaviorSubject(new TranslationProgressMentStatus());
  @Input() actionChanges$: BehaviorSubject<any> = new BehaviorSubject({});
  @Output() iframeLoadedEvent: EventEmitter<any> = new EventEmitter();

  formModel: TranslationFormModel = new TranslationFormModel();
  iFrameContent: any;
  frameId = TranslationFormsFrameId.RightForm;
  unSubscribe$ = new Subject<void>();
  progressStatus: TranslationProgressMentStatus = new TranslationProgressMentStatus();
  isIFrameDirty: boolean = false;
  translationToolNames = TranslationToolNames;

  // for fxRate. need to move into a service after refactoring
  settings: any;
  inputCurrencyPattern = /^(?:\d+,)*\d+(?:\.\d+)?$/;
  arLineClassNames: any = [];
  linesInAPage: any;
  bFixColumnSpacing: boolean = false;
  currencyFormats = [
    {
      value: ',.',
      text: '222,222.343'
    },
    {
      value: '.,',
      text: '222.222,343'
    }
  ];
  fxHashtable = {};
  currencySymbolHashtable = {};
  inputCurrencyFormat = ',.';
  inputThousandsSeparator = ',';
  inputDecimalSeparator = '.';
  outputCurrencyFormat = ',.';
  outputThousandsSeparator = ',';
  outputDecimalSeparator = '.';
  mouseleave: any = [];
  currencySymbolMouseLeave: any = [];
  exchangeRate: any = {};
  rateDateType: any;
  conversionAmount = 1;
  currencyConversionInfo: any = {};
  currencySymbolConversionInfo: any = {};
  private convertedAmount: any;
  private fxTarget: any;
  private currencySymbolTarget: any;
  private firstTimeConvertExchangeRate: boolean = true;
  currencyQueryKey = 'TaxFormCurrencies'; // note: currently fetching from itrce
  currencies$: any;
  currencies: any = [];
  currencySymbols: any = [];
  isApproaching: boolean;
  hours: number;
  minutes: number;
  minutesStr: string;
  seconds: number;
  secondsStr: string;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private translationUIService: TranslationUIService,
              private translationCurrencyConversionService: TranslationCurrencyConversionService,
              private dialogService: DialogService,
              private commandService: CommandService,
              private snackBarService: SnackBarService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private queryStore: Store<IQueryState>,
              @Inject(LOCALE_ID) public locale: string,
              private progressBarService: ProgressBarService,
              private translationService: TranslationService) {
    this.iFrameContent = null;
  }

  ngOnInit() {
    this.formInfo$.pipe(takeUntil(this.unSubscribe$)).subscribe((formInfo: TranslationFormModel) => {
      //console.log('at right:', formInfo);
      if(formInfo && formInfo.hasPreview) { // I have something to show
        if(this.formModel && this.formModel.htmlFileId !== formInfo.htmlFileId) { // invalidate New html
          this.formModel = formInfo;
          this.iFrameContent = this.translationService.createFrame(this.frameId);
          this.resetIframeDirtiness();
          this.generatePageNumbers(this.formModel);
          this.getIFrameOnLoad();
          this.setUpCurrencyConfig();
          this.setTimer();
          this.translationUIService.getHtmlForm(document, this.frameId, this.formModel.htmlFileId);
          //console.log('right form model:', this.formModel);
        }
      } else {
        this.setUpCurrencyConfig();
        this.formModel = formInfo;
        this.iFrameContent = null;
      }
    });
    this.currencies$ = this.queryStore.pipe(select(getQueryResponse, this.currencyQueryKey), takeUntil(this.unSubscribe$));
    this.currencies$.subscribe(currencies => {
      if (currencies) {
        this.currencies = currencies;
        this.currencySymbols = [];
        if (this.currencies && this.currencies.length > 0) {
          this.currencies.forEach(currency => {
            currency.text = currency.currency_code + ' - ' + currency.currency_name;
          });
          this.currencies.sort(function (a, b) {
            if (a.currency_code < b.currency_code) {
              return -1;
            } else if (a.currency_code === b.currency_code) {
              return 0;
            } else {
              return 1;
            }
          });
          this.currencySymbols = this.currencies.map(currency => currency.currency_symbol);
        }
      }
    });
    QueryProvider.invokeQuery(this.queryStore, this.currencyQueryKey, {});
    this.progressStatus$.pipe(takeUntil(this.unSubscribe$)).subscribe((progress: TranslationProgressMentStatus) => {
      if(progress.formId === TranslationForms.RightForm) {
        this.progressStatus = progress;
        //console.log('progress status at right:', progress);
      }
    });

    this.actionChanges$.pipe(takeUntil(this.unSubscribe$)).subscribe(command => {
      this.handleToolActions(command);
    });
  }

  onDownload() {
// todo:
  }

  invalidateTimer() {
    const expiryDate = new Date(this.translationModel.expiryDate);
    const now   = new Date();
    const distance = expiryDate.getTime() - now.getTime();
    // Time calculations for days, hours, minutes and seconds
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    this.hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    this.minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    this.seconds = Math.floor((distance % (1000 * 60)) / 1000);
    this.minutesStr = this.pad(this.minutes, 2);
    this.secondsStr = this.pad(this.seconds, 2);
    this.isApproaching = this.minutes < 10;
    if (distance <= 0) {
      this.invalidateTranslationModel();
    }
  }

  pad(num, size) {
    let s = num + '';
    while (s.length < size) {
      s = '0' + s;
    }
    return s;
  }


  onSaveFxRateChanges() {
    let framennerHtml = this.getFrameInnerHtml(this.frameId);
    let file = new File(['<html>' + framennerHtml + '</html>'], `${StringUtility.getFileNameExcludingExtension(this.formModel.fileName)}.html`, {type: "text/html"});
    this.fuseSplashScreenService.show();
    this.translationService.uploadClientFile(file, this.translationModel.id).pipe(takeUntil(this.unSubscribe$)).subscribe(fileId => {
      let command = new ConvertHtmlToPdfCommand(this.translationModel.id, fileId,
        `${this.formModel.fileName}`
      );
      this.commandService.executeCommand(command).pipe().subscribe(response => {
        this.fuseSplashScreenService.hide();
        if(response) {
          this.snackBarService.showInformation('Your document is processing. You will be notified  once your document is saved and attached to the task.');
          this.resetIframeDirtiness();
        }
      }, err => {
        this.fuseSplashScreenService.hide();
        this.snackBarService.showError('Save failed!!!');
      });
    });
  }

  private getFrameInnerHtml(frameId: string) {
    const iframe = <HTMLIFrameElement>document.getElementById(frameId);
    const iFrameHtml = iframe.contentDocument.querySelector('html');
    const htmlString = iFrameHtml.innerHTML;
    return htmlString;
  }


  generatePageNumbers(formModel: TranslationFormModel) {
    if (formModel && formModel.pageCount > 0) {
      formModel.currentPage = 1;
      formModel.pages = Array(formModel.pageCount).fill(0);
    } else {
      formModel.pages = [];
    }
  }

  private getIFrameOnLoad() {
    this.changeDetectorRef.detectChanges();
    const frame = <HTMLIFrameElement>document.getElementById(this.frameId);
    frame.addEventListener('load', () => {
      //console.log('right frame loaded.');
      this.rightFrameLoaded(frame);
    });

  }

  jumpToPage(event) {
    this.formModel.currentPage = event.target.value;
    const frame = <HTMLIFrameElement>document.getElementById(this.frameId);
    const page = frame.contentDocument.querySelector(`body div[id='${this.formModel.currentPage}']`);
    if (page !== null) {
      page.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }


  // complex calculation part

  onSelectFxRates() {
    if (this.firstTimeConvertExchangeRate) {
      this.warmUpCurrencyData();
    }
    const dialogModel: DialogModel = DialogModel.create(
      'Convert Currencies',
      'FxRateDialogComponent',
      {
        currencyConversionInfo: this.currencyConversionInfo,
        exchangeRate: this.exchangeRate,
        conversionAmount: this.conversionAmount,
        inputCurrencyFormat: this.inputCurrencyFormat,
        outputCurrencyFormat: this.outputCurrencyFormat,
        currencySymbolConversionInfo: this.currencySymbolConversionInfo,
        rateDateType: this.rateDateType,
        settings: this.settings,
        currencies: this.currencies,
        convertedAmount: this.convertedAmount,
        inputThousandsSeparator: this.inputThousandsSeparator,
        inputDecimalSeparator: this.inputDecimalSeparator,
        outputThousandsSeparator: this.outputThousandsSeparator,
        outputDecimalSeparator: this.outputDecimalSeparator,
        inputCurrencyPattern: this.inputCurrencyPattern,
        currencyFormats: this.currencyFormats,
        currencySymbols: this.currencySymbols,
        firstTimeConvertExchangeRate: this.firstTimeConvertExchangeRate
      },
      '900px',
      '900px'
    );
    this.dialogService.showOkCancelDialog(dialogModel).subscribe(res => {
      this.currencyConversionInfo = res.data.input.currencyConversionInfo;
      this.exchangeRate = res.data.input.exchangeRate;
      this.conversionAmount = res.data.input.conversionAmount;
      this.inputCurrencyFormat = res.data.input.inputCurrencyFormat;
      this.outputCurrencyFormat = res.data.input.outputCurrencyFormat;
      this.currencySymbolConversionInfo = res.data.input.currencySymbolConversionInfo;
      this.rateDateType = res.data.input.rateDateType;
      this.settings = res.data.input.settings;
      this.currencies = res.data.input.currencies;
      this.convertedAmount = res.data.input.convertedAmount;
      this.currencyFormats = res.data.input.currencyFormats;
      this.currencySymbols = res.data.input.currencySymbols;
      this.inputThousandsSeparator = res.data.input.inputThousandsSeparator;
      this.inputDecimalSeparator = res.data.input.inputDecimalSeparator;
      this.outputThousandsSeparator = res.data.input.outputThousandsSeparator;
      this.outputDecimalSeparator = res.data.input.outputDecimalSeparator;
      this.inputCurrencyPattern = res.data.input.inputCurrencyPattern;
      this.firstTimeConvertExchangeRate = res.data.input.firstTimeConvertExchangeRate;
      if (res.action === 'OK') {
        this.convertExchangeRates();
      }
    });
  }

  private rightFrameLoaded(rightFrame: HTMLIFrameElement) {
    this.iframeLoadedEvent.emit({frameId: this.frameId});
    this.broadcaseRightFrameLoadedEvent();
  }

  private broadcaseRightFrameLoadedEvent() {
    const iframe = <HTMLIFrameElement>document.getElementById(this.frameId);
    const findElement = iframe.contentDocument.querySelectorAll('.numberword');
    //console.log('numberword', findElement);
    Array.from(findElement).forEach((e: Element) => {
      e.addEventListener('mouseleave', (event) => {//console.log('mouse leave:', event);
        this.mouseleave.push('in');
        setTimeout(() => {
          this.mouseleave.pop();
          if (this.mouseleave.length === 0) {
            this.hideSelectors('fxSelector');
          }
        }, 5000);
      }, false);

      e.addEventListener('mouseenter', (event) => {
        //console.log('mouse enter:', event);
        this.fxTarget = event.target as HTMLSpanElement;
        const elementId = this.getFxTargetElementId();
        let amount = this.fxTarget.innerHTML;
        if (this.fxHashtable[elementId] !== undefined && this.fxHashtable[elementId].originalValue !== undefined) {
          amount = this.fxHashtable[elementId].originalValue;
        }

        if (this.exchangeRate !== undefined && this.exchangeRate.destinationCurrencySymbol !== undefined) {
          if (this.inputCurrencyPattern.test(amount)) {
            const convertedAmount = this.formatCurrency(this.translationCurrencyConversionService.getConvertedAmount(amount, this.exchangeRate, this.inputThousandsSeparator, this.inputDecimalSeparator)
              , 2, this.outputThousandsSeparator, this.outputDecimalSeparator);
            const demands = document.getElementsByClassName('onDemandConvert');
            Array.from(demands).forEach((el: Element) => {
              if (this.currencyConversionInfo.showSymbol === true) {
                el.innerHTML = this.exchangeRate.destinationCurrencySymbol + convertedAmount;
              } else {
                el.innerHTML = convertedAmount;
              }
            });
          } else {
            const demands = document.getElementsByClassName('onDemandConvert');
            Array.from(demands).forEach((el: Element) => {
              el.innerHTML = 'From currency format does not match.';
            });
          }
        } else {
          const demands = document.getElementsByClassName('onDemandConvert');
          Array.from(demands).forEach((el: Element) => {
            el.innerHTML = 'Please select your currency.';
          });
        }
        const fxSelector = document.getElementsByClassName('fxSelector');
        const targetPosition = this.fxTarget.getBoundingClientRect();
        let top = targetPosition.top + iframe.contentWindow.pageYOffset;
        let left = targetPosition.left + iframe.contentWindow.pageXOffset;
        Array.from(fxSelector).forEach((el: any) => {
          el.style.position = 'absolute';
          el.style.marginLeft = targetPosition.marginLeft + 'px';
          el.style.marginTop = '' + targetPosition.marginTop + 'px';
          el.style.top = '' + (top + iframe.offsetTop - iframe.contentWindow.scrollY + targetPosition.height) + 'px';
          el.style.left = '' + (left + iframe.offsetLeft - iframe.contentWindow.scrollX) + 'px';
          el.style.display = 'block';
          el.style.background = '#fafafa';
        });
      }, false);
    });


    const currencySymbolElement = iframe.contentDocument.querySelectorAll('.currencySymbolWord');
    Array.from(currencySymbolElement).forEach((e: Element) => {
      e.addEventListener('mouseleave', (event: any) => {
        this.currencySymbolMouseLeave.push('in');
        setTimeout(() => {
          this.currencySymbolMouseLeave.pop();
          if (this.currencySymbolMouseLeave.length === 0) {
            this.hideSelectors('currencySymbolSelector');
          }
        }, 5000);
      });
      e.addEventListener('mouseenter', (event: any) => {
        const rightFrameBody = iframe.contentDocument.querySelector('body');
        const rightScrollTop = iframe.contentWindow.scrollY;
        this.currencySymbolTarget = event.target as HTMLSpanElement;
        const elementId = this.getcurrencySymbolTargetElementId();
        let currencySymbol = this.currencySymbolTarget.innerHTML;
        if (this.currencySymbolHashtable[elementId] !== undefined && this.currencySymbolHashtable[elementId].originalSymbol !== undefined) {
          currencySymbol = this.currencySymbolHashtable[elementId].originalSymbol;
        }

        if (this.exchangeRate === undefined) {
          const demands = document.getElementsByClassName('onDemandCurrencySymbol');
          Array.from(demands).forEach((el: Element) => {
            el.innerHTML = 'Please select from & to currency Symbol.';
          });
        } else {
          if (currencySymbol !== this.currencySymbolConversionInfo.fromCurrencySymbol) {
            const demands = document.getElementsByClassName('onDemandCurrencySymbol');
            Array.from(demands).forEach((el: Element) => {
              el.innerHTML = 'From currency symbol does not match.';
            });
          } else {
            const demands = document.getElementsByClassName('onDemandCurrencySymbol');
            Array.from(demands).forEach((el: Element) => {
              el.innerHTML = this.currencySymbolConversionInfo.toCurrencySymbol;
            });
          }
        }
        const currencySymbolSelector = document.getElementsByClassName('currencySymbolSelector');
        const targetPosition = this.currencySymbolTarget.getBoundingClientRect();
        let top = targetPosition.top + iframe.contentWindow.pageYOffset;
        let left = targetPosition.left + iframe.contentWindow.pageXOffset;
        Array.from(currencySymbolSelector).forEach((el: any) => {
          el.style.position = 'absolute';
          el.style.marginLeft = targetPosition.marginLeft + 'px';
          el.style.marginTop = '' + targetPosition.marginTop + 'px';
          el.style.top = '' + (top + iframe.offsetTop - iframe.contentWindow.scrollY + targetPosition.height) + 'px';
          el.style.left = '' + (left + iframe.offsetLeft - iframe.contentWindow.scrollX) + 'px';
          el.style.display = 'block';
          el.style.background = '#fafafa';
        });
      });
    });
  }

  revertCurrencySymbol() {
    const elementId = this.getcurrencySymbolTargetElementId();
    if (this.currencySymbolHashtable[elementId] !== undefined) {
      const originalSymbol = this.currencySymbolHashtable[elementId].originalSymbol;
      this.currencySymbolHashtable[elementId] = undefined;
      this.currencySymbolTarget.innerHTML = originalSymbol;
    }
    this.hideSelectors('currencySymbolSelector');
  }

  convertCurrencySymbol() {
    const elementId = this.getcurrencySymbolTargetElementId();
    let currencySymbol = this.currencySymbolTarget.innerHTML;

    if (this.currencySymbolHashtable[elementId] !== undefined && this.currencySymbolHashtable[elementId].originalSymbol !== undefined) {
      currencySymbol = this.currencySymbolHashtable[elementId].originalSymbol;
    }

    if (this.currencySymbolConversionInfo !== undefined && currencySymbol === this.currencySymbolConversionInfo.fromCurrencySymbol) {
      this.currencySymbolHashtable[elementId] = {
        'originalSymbol': currencySymbol,
        'convertedSymbol': '',
        'elementId': elementId
      };
      this.currencySymbolHashtable[elementId].convertedSymbol = this.currencySymbolConversionInfo.toCurrencySymbol;
      this.currencySymbolTarget.innerHTML = this.currencySymbolConversionInfo.toCurrencySymbol;
    }
    this.hideSelectors('currencySymbolSelector');
  }

  getFxTargetElementId() {
    const elementClass = this.fxTarget.getAttribute('class');
    return '.' + elementClass.split(' ').join('.');
  }

  getcurrencySymbolTargetElementId() {
    const elementClass = this.currencySymbolTarget.getAttribute('class');
    return '.' + elementClass.split(' ').join('.');
  }

  private hideSelectors(selectorClass: string) {
    const selector = document.getElementsByClassName(selectorClass);
    Array.from(selector).forEach((el: any) => {
      el.style.display = 'none';
    });
  }

  convertFx() {
    const elementId = this.getFxTargetElementId();
    let amount = this.fxTarget.innerHTML;

    if (this.fxHashtable[elementId] !== undefined && this.fxHashtable[elementId].originalValue !== undefined) {
      amount = this.fxHashtable[elementId].originalValue;
    }

    if (this.inputCurrencyPattern.test(amount)) {
      this.fxHashtable[elementId] = {'originalValue': amount, 'convertedValue': '', 'elementId': elementId};
      const convertedAmount = this.formatCurrency(this.translationCurrencyConversionService.getConvertedAmount(amount, this.exchangeRate, this.inputThousandsSeparator,
        this.inputDecimalSeparator), 2, this.outputThousandsSeparator, this.outputDecimalSeparator);
      this.fxHashtable[elementId].convertedValue = convertedAmount;
      if (this.exchangeRate !== undefined && this.exchangeRate.destinationCurrencySymbol !== undefined && this.currencyConversionInfo.showSymbol) {
        if (this.settings.isInsertNote === true) {
          this.fxTarget.innerHTML = this.exchangeRate.destinationCurrencySymbol + convertedAmount;
        } else {
          this.fxTarget.innerHTML = this.exchangeRate.destinationCurrencySymbol + convertedAmount;
        }
      } else if (this.exchangeRate !== undefined && this.exchangeRate.destinationCurrencySymbol !== undefined) {
        if (this.settings.isInsertNote === true) {
          this.fxTarget.innerHTML = convertedAmount;
        } else {
          this.fxTarget.innerHTML = convertedAmount;
        }
      }
    }
    this.hideSelectors('fxSelector');
  }

  revertFxConversion() {
    const elementId = this.getFxTargetElementId();
    if (this.fxHashtable[elementId] !== undefined) {
      const originalValue = this.fxHashtable[elementId].originalValue;
      this.fxHashtable[elementId] = undefined;
      this.fxTarget.innerHTML = originalValue;
    }
    this.hideSelectors('fxSelector');
  }


  private warmUpCurrencyData() {
    this.conversionAmount = 1;
    this.currencyConversionInfo = {};
    this.currencyConversionInfo.formId = this.formModel.htmlFileId;
    this.currencyConversionInfo.fromCurrencyCode = 'EUR';
    this.currencyConversionInfo.toCurrencyCode = 'USD';
    this.currencyConversionInfo.showSymbol = false;
    const currentDate = new Date();
    this.currencyConversionInfo.sportRateDate = currentDate;
    this.currencyConversionInfo.averageRateFromDate = new Date(currentDate.getFullYear(), 0, 1);
    this.currencyConversionInfo.averageRateFromTo = currentDate;
    this.currencyConversionInfo.notes = [];
    this.rateDateType = 'Sport'; // Average;
    this.currencyConversionInfo.selectedNoteId = '';
    this.currencySymbolConversionInfo = {
      fromCurrencySymbol: '€',
      toCurrencySymbol: '$'
    };
  }

  convertExchangeRates() {
    let count = 0;
    const rightFrame = <HTMLIFrameElement>document.getElementById(this.frameId);
    if (this.settings.applyToAll === true) {
      const numbers = rightFrame.contentDocument.querySelectorAll('.numberWord');
      Array.from(numbers).forEach((number: HTMLSpanElement) => {
        const elementClass = number.getAttribute('class');
        const elementId = '.' + elementClass.split(' ').join('.');
        let amount = number.innerHTML;
        if (this.fxHashtable[elementId] !== undefined && this.fxHashtable[elementId].originalValue !== undefined) {
          amount = this.fxHashtable[elementId].originalValue;
        }
        if (this.inputCurrencyPattern.test(amount)) {
          this.fxHashtable[elementId] = {'originalValue': amount, 'convertedValue': '', 'elementId': elementId};
          const convertedCurrencyAmount = this.formatCurrency(this.translationCurrencyConversionService.getConvertedAmount(amount, this.exchangeRate, this.inputThousandsSeparator, this.inputDecimalSeparator),
            2, this.outputThousandsSeparator, this.outputDecimalSeparator);
          this.fxHashtable[elementId].convertedValue = convertedCurrencyAmount;
          if (this.notUndefinedAndNull(this.exchangeRate) && this.notUndefinedAndNull(this.exchangeRate.destinationCurrencySymbol) && this.currencyConversionInfo.showSymbol) {
            count++;
            number.innerHTML = this.exchangeRate.destinationCurrencySymbol + convertedCurrencyAmount;
          } else {
            count++;
            number.innerHTML = convertedCurrencyAmount;
          }
        }
      });
    } else if (this.settings.applyToHighlighted === true) {
      const contents = rightFrame.contentDocument;
      for (const entry of (<any>Object).entries(this.fxHashtable)) {
        const [key, value] = entry;
        const storedItem = this.fxHashtable[key];
        const amount = ''; // in itrce: apply to highlighted commented
        const convertedAmount = this.formatCurrency(this.translationCurrencyConversionService.getConvertedAmount(amount, this.exchangeRate, this.inputThousandsSeparator, this.inputDecimalSeparator),
          2, this.outputThousandsSeparator, this.outputDecimalSeparator);
        storedItem.convertedValue = convertedAmount;
        if (this.exchangeRate !== undefined && this.exchangeRate.destinationCurrencySymbol !== undefined && this.currencyConversionInfo.showSymbol) {
          const highlighted = contents.querySelectorAll(key);
          Array.from(highlighted).forEach((el: Element) => {
            count++;
            el.innerHTML = this.exchangeRate.destinationCurrencySymbol + convertedAmount;
          });
        } else {
          const highlighted = contents.querySelectorAll(key);
          Array.from(highlighted).forEach((el: Element) => {
            count++;
            el.innerHTML = convertedAmount;
          });
        }
      }
    }

    if (this.settings.isInsertNote === true) {
      const ele = rightFrame.contentDocument.querySelectorAll('div.fxRatefootNote');
      Array.from(ele).forEach((el: Element) => {
        el.remove();
      });
      const lastDoc = rightFrame.contentDocument.querySelectorAll(`div[id="${this.formModel.pageCount}"]`);
      const last = lastDoc[lastDoc.length - 1];

      Array.from(this.currencyConversionInfo.notes).forEach((item: any) => {
        const fxRatefootNote = document.createElement('div');
        fxRatefootNote.classList.add('fxRatefootNote');
        fxRatefootNote.style.fontSize = '13px';
        fxRatefootNote.style.lineHeight = 'normal';
        fxRatefootNote.style.color = '#333';
        fxRatefootNote.style.padding = '3px';
        fxRatefootNote.style.marginLeft = '5px';
        fxRatefootNote.style.overflow = 'hidden';
        const fxFootNote = last.appendChild(fxRatefootNote);
        count++;
        fxFootNote.innerHTML = ('<div style=\'float:left;width:25px\'>' + item.id + '</div>' + ' <div style=\'float:left;width:80%\'>' + item.text + '</div>');
      });
    }
    if (this.settings.applyToAllSymbols === true) {
      const symbols = rightFrame.contentDocument.querySelectorAll('.currencySymbolWord');
      Array.from(symbols).forEach((singleSymbol) => {
        const elementClass = singleSymbol.getAttribute('class');
        const elementId = '.' + elementClass.split(' ').join('.');
        let symbol = singleSymbol.innerHTML;
        if (this.currencySymbolHashtable[elementId] !== undefined && this.currencySymbolHashtable[elementId].originalSymbol !== undefined) {
          symbol = this.currencySymbolHashtable[elementId].originalSymbol;
        }

        if (this.currencySymbolConversionInfo !== undefined && symbol === this.currencySymbolConversionInfo.fromCurrencySymbol) {
          this.currencySymbolHashtable[elementId] = {
            'originalSymbol': symbol,
            'convertedSymbol': '',
            'elementId': elementId
          };
          this.currencySymbolHashtable[elementId].convertedSymbol = this.currencySymbolConversionInfo.toCurrencySymbol;
          count++;
          singleSymbol.innerHTML = this.currencySymbolConversionInfo.toCurrencySymbol;
        }
      });
    }
    if(count > 0) {
      this.makeIframeDirty();
    }
  }

  private notUndefinedAndNull(value: any): Boolean {
    return value !== undefined && value !== null;
  }

  formatCurrency(amount, fractionSize, thoursandsSeparator, decimalSeparator) {
    let convertedNumber = this.numberFilter(amount, fractionSize);
    const decimalPointIndex = convertedNumber.lastIndexOf('.');
    if (thoursandsSeparator !== undefined) {
      convertedNumber = convertedNumber.split(',').join(thoursandsSeparator);
    }
    if (decimalSeparator !== undefined && decimalPointIndex >= 0) {
      convertedNumber = convertedNumber.substring(0, decimalPointIndex) +
        decimalSeparator +
        convertedNumber.substring(decimalPointIndex + 1, convertedNumber.length);
    }
    return convertedNumber;
  }

  numberFilter(number, fractionSize): string {
    return (number == null) ? number : formatNumber(number, this.locale, `1.${fractionSize}-${fractionSize}`);
  }

  onFixSpacing() {
    if (this.formModel.pageCount > 0) {
      this.progressBarService.showProgressBar((Math.trunc((1 / this.formModel.pageCount) * 100)), '', false);
      this.processPage(1);
    }
  }

  clearAll() {
    this.progressBarService.hideProgressBar();
    this.arLineClassNames = null;
    this.linesInAPage = null;
    this.bFixColumnSpacing = false;
  }

  doFinalTasks() {
    this.clearAll();
  }

  buildUpLineClassNames(clsName) {
    if (this.arLineClassNames.indexOf(clsName) === -1) {
      this.arLineClassNames.push(clsName);
    }
  }

  fixSpacing(lastWordBox: any, newWord: any, bforceStyle: any) {
    //Each Word
    let rect = newWord.getBoundingClientRect();
    const w = rect.width;
    let l = rect.left;
    if (w == 0) {
      newWord.remove();
      return;
    }
    else if (newWord.innerHTML === '') {
      return;
    } else if (isNaN(l)) {
      l = 0;
    }

    let minLeft = lastWordBox.AnchorX + lastWordBox.Width + 3; //magic number 3 used for adjacent word spacing
    if (l < minLeft) {
      l = minLeft;
    }

    if (!this.bFixColumnSpacing || bforceStyle) {
      //newWord.css({ left: l, width: w });
      newWord.style.left = l + 'px';
      newWord.style.width = w + 'px';
    }
    lastWordBox.AnchorX = l;
    lastWordBox.Width = w;
  }

  processLine(pageNumber, lineItemIndex) {
    let item = this.arLineClassNames[lineItemIndex];
    //Each Line
    let lastWordBox = {AnchorX: 0, Width: 0};
    let itemDOM = Array.from(this.linesInAPage).filter((value: any, i: number) => value.className == item);
    let lastWord;
    itemDOM.forEach((item: HTMLSpanElement) => {
      Array.from(item.children).forEach((el: any) => {
        lastWord = el;
        this.fixSpacing(lastWordBox, lastWord, false);
      });

    });
    if (++lineItemIndex < this.arLineClassNames.length) {
      setTimeout(() => {
        this.processLine(pageNumber, lineItemIndex)
      }, 1);
    }
    else {
      if (++pageNumber <= this.formModel.pageCount) {
        this.processPage(pageNumber);
      }
      else {
        this.doFinalTasks();
      }
    }
  }

  processPage(pageNumber) {
    this.progressBarService.setProgressBarData(Math.trunc((pageNumber / this.formModel.pageCount) * 100));
    const frame = <HTMLIFrameElement>document.getElementById(this.frameId);
    const frameContent = frame.contentDocument;
    if (pageNumber > this.formModel.pageCount) {
      this.doFinalTasks();
      return;
    }

    let elm = 'word';
    if (this.bFixColumnSpacing) {
      elm = 'column';
    }

    this.arLineClassNames = [];
    const query = `div[id="${pageNumber}"] span[class^="page${pageNumber}Line"]`;
    this.linesInAPage = frameContent.querySelectorAll(query);
    Array.from(this.linesInAPage).forEach((el: Element) => {
      this.buildUpLineClassNames(el.className);
    });

    if (this.arLineClassNames.length > 0)
      this.processLine(pageNumber, 0);
    else {
      this.processPage(++pageNumber);
    }
  }


  onResetFxRate() {
    const iframe = <HTMLIFrameElement>document.getElementById(this.frameId);
    const contents = iframe.contentDocument;
    for (const key in this.fxHashtable) {
      const storedItem = this.fxHashtable[key];
      if (storedItem !== undefined) {
        const convertedAmount = storedItem.originalValue;
        storedItem.convertedValue = convertedAmount;
        const elem = contents.querySelectorAll(key);
        Array.from(elem).forEach((el: Element) => {
          el.innerHTML = convertedAmount;
        });
      }
      this.fxHashtable[key] = undefined;
    }

    const noteElements = iframe.contentDocument.querySelectorAll('div.fxRatefootNote');
    Array.from(noteElements).forEach((ele: Element) => {
      ele.remove();
    });
  }

  ngAfterViewInit(): void {
    const fxSelector = document.getElementsByClassName('fxSelector');
    Array.from(fxSelector).forEach((el: any) => {
      el.style.display = 'none';
    });
    const currencySymbolSelector = document.getElementsByClassName('currencySymbolSelector');
    Array.from(currencySymbolSelector).forEach((el: any) => {
      el.style.display = 'none';
    });
  }

  private setUpCurrencyConfig() {
    this.settings = {};
    this.settings.applyToAll = false;
    this.settings.applyToAllSymbols = false;
    this.settings.applyToHighlighted = false;
    this.settings.isInsertNote = false;
    this.fxHashtable = {};
    this.currencySymbolHashtable = {};
    this.firstTimeConvertExchangeRate = true;
  }

  private handleToolActions(command: any) {
    if(command) {
      switch (command.name) {
        case 'FxRateCommand':
          this.onSelectFxRates();
          break;
        case 'FixSpacingCommand':
          this.onFixSpacing();
          break;
        case 'ResetCommand':
          this.onResetFxRate();
          break;
      }
    }
  }

  private makeIframeDirty() {
    this.isIFrameDirty = true;
  }

  private resetIframeDirtiness() {
    this.isIFrameDirty = false;
  }

  private setTimer() {
    //console.log('translation model:', this.translationModel);
    if(this.translationModel && this.translationModel.expiryDate) {
      this.invalidateTimer();
      setInterval(function() {
        this.invalidateTimer();
      }.bind(this), 1000);
    }
  }

  private invalidateTranslationModel() {
    // todo: invalidate translation model
  }
}
